import { omit } from "lodash-es";

import type { FacetedSearchUrlConfig } from "@/lib/faceted-search-url";
import { buildFacetedSearchState, writeFacetedSearchState } from "@/lib/faceted-search-url";
import { getSearchSerialization } from "@garimpa/lib/searchSerialization";
import type {
  RangeMatcher,
  ReleaseSearchFilter,
  ReleaseSearchFilterKey,
  StringMatcher,
} from "@garimpa/lib/searchState";
import { undefinedless } from "@garimpa/lib/undefinedless";

type SearchRecord = Record<string, unknown>;
type FilterMode = "set" | "toggle";
type FilterValue = string | number | readonly [number] | readonly [number | null, number | null];
type DirectReleaseSearchFilterKey = Exclude<ReleaseSearchFilterKey, "artist.country">;

type FilterValueUpdate =
  | {
      mode?: FilterMode;
      value: FilterValue;
    }
  | {
      mode: "clear";
      value?: never;
    };

export type FacetFilterTarget = {
  config: FacetedSearchUrlConfig;
  key: ReleaseSearchFilterKey;
  kind?: "facet";
} & FilterValueUpdate;

export type ParamFilterTarget = {
  key: string;
  kind: "param";
} & FilterValueUpdate;

export type FilterUrlTarget = FacetFilterTarget | ParamFilterTarget;

interface FilterSearchInput {
  currentSearch?: SearchRecord;
  target: FilterUrlTarget;
}

interface FilterHrefInput extends FilterSearchInput {
  basePath: string;
}

interface FilterUrlInput extends FilterHrefInput {
  origin: string | URL;
}

interface FilterLinkTargetInput extends FilterSearchInput {
  params?: Record<string, string>;
  to: string;
}

export type FilterLinkTarget = {
  params?: Record<string, string>;
  search: SearchRecord;
  to: string;
};

const { stringifySearch } = getSearchSerialization();

export function buildFilterSearch(input: FilterSearchInput): SearchRecord {
  const currentSearch = normalizeSearchRecord(input.currentSearch);
  return input.target.kind === "param"
    ? buildParamSearch(currentSearch, input.target)
    : buildFacetSearch(currentSearch, input.target);
}

export function buildFilterHref(input: FilterHrefInput): string {
  const query = stringifySearch(buildFilterSearch(input));
  return `${input.basePath}${query}`;
}

export function buildFilterUrl(input: FilterUrlInput): string {
  return new URL(buildFilterHref(input), input.origin).href;
}

export function buildFilterLinkTarget(input: FilterLinkTargetInput): FilterLinkTarget {
  return {
    to: input.to,
    params: input.params,
    search: buildFilterSearch(input),
  };
}

function buildFacetSearch(currentSearch: SearchRecord, target: FacetFilterTarget) {
  const state = buildFacetedSearchState(currentSearch, target.config) ?? {};

  return undefinedless(
    writeFacetedSearchState(
      currentSearch,
      {
        ...state,
        filter:
          target.mode === "clear"
            ? withoutFacetKey(state.filter ?? {}, target.key)
            : withFacetValue(state.filter, target),
        page: undefined,
      },
      target.config,
    ),
  );
}

function buildParamSearch(currentSearch: SearchRecord, target: ParamFilterTarget) {
  const nextValue = nextParamValue(currentSearch[target.key], target);

  return undefinedless({
    ...currentSearch,
    [target.key]: nextValue,
    page: target.key === "page" ? nextValue : undefined,
  });
}

function withFacetValue(
  filter: ReleaseSearchFilter | undefined,
  target: FacetFilterTarget & { value: FilterValue },
): ReleaseSearchFilter {
  const currentFilter = filter ?? {};

  if (target.mode === "toggle" && facetHasValue(currentFilter, target.key, target.value)) {
    return withoutFacetValue(currentFilter, target.key, target.value);
  }

  return withFacetKeyValue(currentFilter, target.key, target.value);
}

function withoutFacetValue(
  filter: ReleaseSearchFilter,
  key: ReleaseSearchFilterKey,
  value: FilterValue,
): ReleaseSearchFilter {
  if (key === "artist.country") {
    return withArtistCountryMatcher(filter, removeMatcherValue(filter.artist?.country, value));
  }

  if (key === "year") {
    return withoutFacetKey(filter, key);
  }

  const matcher = removeMatcherValue(filter[key], value);
  return matcher ? { ...filter, [key]: matcher } : withoutFacetKey(filter, key);
}

function withoutFacetKey(
  filter: ReleaseSearchFilter,
  key: ReleaseSearchFilterKey,
): ReleaseSearchFilter {
  if (key === "artist.country") {
    return withArtistCountryMatcher(filter, undefined);
  }

  if (key === "year") {
    return omit(filter, "year");
  }

  return omit(filter, key as DirectReleaseSearchFilterKey);
}

function nextParamValue(currentValue: unknown, target: ParamFilterTarget) {
  if (target.mode === "clear") return undefined;
  if (target.mode === "toggle" && isSameParamValue(currentValue, target.value)) return undefined;
  return normalizeParamValue(target.value);
}

function withFacetKeyValue(
  filter: ReleaseSearchFilter,
  key: ReleaseSearchFilterKey,
  value: FilterValue,
): ReleaseSearchFilter {
  if (key === "artist.country") {
    return {
      ...filter,
      artist: { ...filter.artist, country: normalizeStringMatcher(value) },
    };
  }

  if (key === "year") {
    const year = normalizeYearMatcher(value);
    return year ? { ...filter, year } : filter;
  }

  return { ...filter, [key]: normalizeStringMatcher(value) };
}

function withArtistCountryMatcher(
  filter: ReleaseSearchFilter,
  matcher: StringMatcher | undefined,
): ReleaseSearchFilter {
  if (matcher) {
    return {
      ...filter,
      artist: { ...filter.artist, country: matcher },
    };
  }

  if (!filter.artist) return filter;

  const artist = omit(filter.artist, "country");
  if (Object.keys(artist).length) {
    return { ...filter, artist };
  }

  return omit(filter, "artist");
}

function facetHasValue(
  filter: ReleaseSearchFilter,
  key: ReleaseSearchFilterKey,
  value: FilterValue,
): boolean {
  if (key === "year") return sameYearMatcher(filter.year, normalizeYearMatcher(value));
  const matcher = key === "artist.country" ? filter.artist?.country : filter[key];
  return matcherValues(matcher).includes(String(Array.isArray(value) ? value[0] : value));
}

function removeMatcherValue(
  matcher: StringMatcher | undefined,
  value: FilterValue,
): StringMatcher | undefined {
  const removeValue = String(Array.isArray(value) ? value[0] : value);
  if (typeof matcher === "string") return matcher === removeValue ? undefined : matcher;
  if (Array.isArray(matcher)) {
    const values = matcher.filter((item) => item !== removeValue);
    if (values.length === 0) return undefined;
    return values.length === 1 ? values[0] : values;
  }
  if (matcher && "all" in matcher) {
    const values = matcher.all.filter((item) => item !== removeValue);
    if (values.length === 0) return undefined;
    return { all: values };
  }
  return matcher;
}

function matcherValues(matcher: StringMatcher | undefined): string[] {
  if (typeof matcher === "string") return [matcher];
  if (Array.isArray(matcher)) return matcher;
  return matcher?.all ?? [];
}

function normalizeStringMatcher(value: FilterValue): StringMatcher {
  return String(Array.isArray(value) ? value[0] : value);
}

function normalizeYearMatcher(value: FilterValue): RangeMatcher | undefined {
  if (Array.isArray(value)) {
    if (value.length === 1) {
      const year = finiteNumber(value[0]);
      return year == null ? undefined : [year];
    }

    const from = value[0] == null ? null : finiteNumber(value[0]);
    const to = value[1] == null ? null : finiteNumber(value[1]);
    return from == null && to == null ? undefined : [from, to];
  }

  const year = finiteNumber(value);
  return year == null ? undefined : [year];
}

function sameYearMatcher(left: RangeMatcher | undefined, right: RangeMatcher | undefined) {
  if (!left || !right || left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function normalizeParamValue(value: FilterValue) {
  if (!Array.isArray(value)) return value;
  return value.filter((item) => item != null);
}

function isSameParamValue(currentValue: unknown, nextValue: FilterValue) {
  if (Array.isArray(nextValue)) {
    return JSON.stringify(normalizeParamValue(nextValue)) === JSON.stringify(currentValue);
  }
  return String(currentValue ?? "") === String(nextValue);
}

function finiteNumber(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeSearchRecord(value: unknown): SearchRecord {
  return value && typeof value === "object" ? { ...(value as SearchRecord) } : {};
}
